import { subscribeBackIntentChange } from './backIntentBus.js';
import { getEscapeCloseStackSize, invokeTopEscapeClose } from './useEscapeCloseStack.js';
import { getAppBackStackSize, invokeTopAppBack } from './useAppBackButton.js';

/** Marker copied onto history.state so overlay / nested Back can be intercepted. */
export const APP_BACK_GUARD = 'rentalyzerAppBack';

export const APP_BACK_BUTTON_ATTR = 'data-rentalyzer-back';

let installed = false;
let win = null;
let guardDepth = 0;
let pendingSilentPops = 0;
let syncScheduled = false;
let unsubscribeBus = null;
let previousScrollRestoration = null;
let lastWasDummy = false;
let originalPushState = null;
let originalReplaceState = null;

function isGuardState(state) {
  return Boolean(state && typeof state === 'object' && state[APP_BACK_GUARD] === true);
}

function queryVisibleAppBackButton() {
  const doc = win?.document;
  if (!doc?.querySelectorAll) return null;
  const nodes = doc.querySelectorAll(`[${APP_BACK_BUTTON_ATTR}]`);
  for (let i = nodes.length - 1; i >= 0; i -= 1) {
    const el = nodes[i];
    if (el && typeof el.click === 'function' && (el.offsetWidth || el.offsetHeight || el.getClientRects?.().length)) {
      return el;
    }
  }
  return nodes.length ? nodes[nodes.length - 1] : null;
}

function needGuard() {
  // Main screens use HashRouter `?rz=` — do not dummy-trap a visible ← Retour.
  // Dummy entries are only for overlays and nested same-URL views.
  return getEscapeCloseStackSize() > 0 || getAppBackStackSize() > 0;
}

function rememberDummyFlag() {
  lastWasDummy = isGuardState(win?.history?.state);
  guardDepth = lastWasDummy ? 1 : 0;
}

function pushGuard() {
  if (!win || lastWasDummy) return;
  const prev = win.history.state;
  const prevObj = prev && typeof prev === 'object' && !Array.isArray(prev) ? prev : {};
  const push = originalPushState || win.history.pushState.bind(win.history);
  push(
    { ...prevObj, [APP_BACK_GUARD]: true },
    '',
    win.location.href,
  );
  lastWasDummy = true;
  guardDepth = 1;
}

function popGuardSilently() {
  if (!win || !lastWasDummy) return;
  if (!isGuardState(win.history.state)) {
    lastWasDummy = false;
    guardDepth = 0;
    return;
  }
  pendingSilentPops += 1;
  lastWasDummy = false;
  guardDepth = 0;
  win.history.back();
}

function syncGuard() {
  if (!win) return;
  const need = needGuard();
  if (need && !lastWasDummy) pushGuard();
  else if (!need && lastWasDummy) popGuardSilently();
}

function scheduleSync() {
  if (syncScheduled) return;
  syncScheduled = true;
  queueMicrotask(() => {
    syncScheduled = false;
    syncGuard();
  });
}

function clickVisibleAppBackButton() {
  const btn = queryVisibleAppBackButton();
  if (!btn) return false;
  btn.click();
  return true;
}

function onPopState() {
  if (pendingSilentPops > 0) {
    pendingSilentPops -= 1;
    rememberDummyFlag();
    return;
  }

  const leftDummy = lastWasDummy;
  rememberDummyFlag();

  // Left a real HashRouter entry (?rz=dashboard → home). Never steal that POP.
  if (!leftDummy) return;

  if (invokeTopEscapeClose()) {
    scheduleSync();
    return;
  }
  if (clickVisibleAppBackButton()) {
    scheduleSync();
    return;
  }
  if (invokeTopAppBack()) {
    scheduleSync();
  }
}

function wrapHistory() {
  const hist = win.history;
  originalPushState = hist.pushState.bind(hist);
  originalReplaceState = hist.replaceState.bind(hist);
  hist.pushState = function pushState(state, title, url) {
    originalPushState(state, title, url);
    rememberDummyFlag();
  };
  hist.replaceState = function replaceState(state, title, url) {
    let next = state;
    const hrefUnchanged = !url || url === win.location.href;
    if (
      lastWasDummy
      && hrefUnchanged
      && next
      && typeof next === 'object'
      && !Array.isArray(next)
    ) {
      next = { ...next, [APP_BACK_GUARD]: true };
    }
    originalReplaceState(next, title, url);
    rememberDummyFlag();
  };
}

function unwrapHistory() {
  if (!win || !originalPushState) return;
  win.history.pushState = originalPushState;
  win.history.replaceState = originalReplaceState;
  originalPushState = null;
  originalReplaceState = null;
}

/**
 * Intercepts Android/browser Back only for dummy overlay entries.
 * Main screens use `?rz=` HashRouter history — those POPs must reach React Router.
 */
export function installAppBackHistory(targetWindow) {
  const next = targetWindow || (typeof window !== 'undefined' ? window : null);
  if (!next?.history || installed) return;
  win = next;
  installed = true;
  if (next.history.scrollRestoration) {
    previousScrollRestoration = next.history.scrollRestoration;
    next.history.scrollRestoration = 'manual';
  }
  wrapHistory();
  rememberDummyFlag();
  next.addEventListener('popstate', onPopState, true);
  unsubscribeBus = subscribeBackIntentChange(scheduleSync);
  scheduleSync();
}

export function resetAppBackHistoryForTests() {
  if (win) {
    win.removeEventListener('popstate', onPopState, true);
    unwrapHistory();
    if (previousScrollRestoration && win.history.scrollRestoration) {
      win.history.scrollRestoration = previousScrollRestoration;
    }
  }
  unsubscribeBus?.();
  unsubscribeBus = null;
  installed = false;
  win = null;
  guardDepth = 0;
  pendingSilentPops = 0;
  syncScheduled = false;
  previousScrollRestoration = null;
  lastWasDummy = false;
}

export function getAppBackGuardDepthForTests() {
  return guardDepth;
}
